import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { getHotels } from "@/lib/api";
import { Hotel as HotelIcon } from "lucide-react";

export const HotelClients = () => {
  const { t } = useLang();
  const [hotels, setHotels] = useState([]);

  useEffect(() => {
    getHotels().then(setHotels).catch(() => setHotels([]));
  }, []);

  return (
    <section id="hotels" className="relative overflow-hidden bg-white py-24 sm:py-28" data-testid="hotels">
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B87B0] mb-5">{t.hotels.overline}</p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#090A0C]"
            >
              {t.hotels.title}
            </motion.h2>
          </div>
          <p className="lg:col-span-4 text-base sm:text-lg text-[#4B5563] leading-relaxed">{t.hotels.lead}</p>
        </div>

        {hotels.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#E5E7EB] bg-white py-20 text-center" data-testid="hotels-empty">
            <HotelIcon className="mx-auto mb-4 text-[#CBD5E1]" size={40} />
            <p className="text-[#4B5563]">{t.hotels.empty}</p>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-4">
            {hotels.map((h, i) => (
              <div key={h.id} className="w-[calc(50%-0.5rem)] sm:w-[calc(33.333%-0.67rem)] lg:w-[calc(25%-0.75rem)]">
                <HotelTile h={h} i={i} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const HotelTile = ({ h, i }) => {
  const inner = (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`hotel-${h.id}`}
      className="group flex h-32 items-center justify-center rounded-md border border-[#E5E7EB] bg-white p-6 shadow-[0_6px_24px_rgba(9,10,12,0.04)] transition-colors hover:border-[#00C3FF]"
    >
      {h.logo_url ? (
        <img src={h.logo_url} alt={h.name} title={h.name} className="max-h-16 w-auto max-w-[80%] object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
      ) : (
        <div className="text-center">
          <HotelIcon size={22} className="mx-auto mb-1.5 text-[#00C3FF]" />
          <span className="font-display font-bold tracking-tight text-sm text-[#090A0C]">{h.name}</span>
        </div>
      )}
    </motion.div>
  );
  return h.website ? (
    <a href={h.website} target="_blank" rel="noopener noreferrer">{inner}</a>
  ) : inner;
};
